import { focusOutline } from 'shared/assets/styles';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css } from 'styled-components';

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;

  overflow: hidden;

  & button:focus {
    ${focusOutline};
  }
`;

export const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;

  display: flex;
  flex-direction: column;

  overflow-y: auto;
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};

  padding: ${({ theme }) => `${theme.spacing(3)}`};
`;

export const TitleColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Title = styled.h2`
  ${({ theme }) => typographyToCss(theme.typography.subtitle2)};
  margin: 0;
  overflow-wrap: anywhere;
`;

// the actions popup opens off this box, so it must not scroll away with the title
export const HeaderMenuAnchor = styled.div`
  position: relative;
  flex-shrink: 0;
`;

// The field grid: column 1 is the icon+label, column 2 the control. Rows are
// `display: contents` so every label lines up on one column across the popup.
export const Body = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(2)};
  row-gap: ${({ theme }) => theme.spacing(2)};

  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
`;

export const Section = styled.div`
  display: contents;
`;

// `display: contents` drops this box, so the Label and the value become Body's own grid
// items — per-row alignment has to be set on those children, never on this element.
export const Item = styled.div<{
  $autoWidthField?: boolean;
  $alignTop?: boolean;
}>`
  display: contents;

  ${({ $alignTop }) =>
    $alignTop &&
    css`
      & > * {
        align-self: start;
      }
    `};

  & > :last-child {
    ${({ $autoWidthField }) =>
      $autoWidthField
        ? css`
            justify-self: start;
          `
        : css`
            width: 100%;
          `};
  }
`;

export const Label = styled.label`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;
  min-height: 2.8rem;

  & > svg {
    width: 1.6rem;
    height: 1.6rem;
    min-width: 1.6rem;
    flex-shrink: 0;
    color: ${({ theme }) => theme.palette.grey[600]};
  }
`;

export const LabelText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  min-height: 2.5rem;
`;

export const DateTimeFields = styled.div`
  flex: 1;
  min-width: 0;

  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

// the trigger's own popup anchor — the repeat builder opens off this box
export const TriggerAnchor = styled.div`
  min-width: 6.4rem;
`;

// stands in for the row icon on the calendar row, tinted with the calendar's own colour
export const CalendarDot = styled.span<{ $color: string }>`
  display: block;
  width: 1rem;
  height: 1rem;
  margin: 0 0.3rem;
  flex-shrink: 0;

  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

// calendar picker and availability share one line; the picker takes the slack
export const CalendarFields = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  min-width: 0;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }
`;

export const WarningBanner = styled.div`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};

  margin: ${({ theme }) => `0 ${theme.spacing(4)} ${theme.spacing(4)}`};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme }) => theme.palette.warning.light};
  color: ${({ theme }) => theme.palette.warning.dark};
`;

export const Value = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 2.8rem;
  // padding: 4px 0;
`;

// a row whose value is still loading — sized to the control it replaces so the
// popup does not reflow when the detail fetch lands
export const FieldSkeleton = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 2.8rem;
`;

export const ReadOnlyValue = styled.div`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  min-width: 0;
  overflow-wrap: anywhere;
  // provider descriptions carry their own line breaks; the default collapses them into one run
  white-space: pre-wrap;
  color: ${({ theme }) => theme.palette.text.primary};
`;

// spans the whole value column so a click beside the maps link still reopens the input
export const LocationReadView = styled.div<{ $canEdit: boolean }>`
  display: flex;
  align-items: center;
  flex: 1;
  align-self: stretch;
  min-width: 0;

  ${({ $canEdit }) =>
    $canEdit &&
    css`
      cursor: text;
    `};
`;

// keeps the link inline; a flex item would blockify and swallow the click beside it
export const LocationText = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  width: 100%;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const LocationLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.primary.main};

  &:hover {
    text-decoration: underline;
  }
`;

// The video call row stacks: which conferencing system minted the link, the link itself, and — only
// where a swap is possible — the action or the note saying one is armed.
export const VideoCallValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
`;

export const VideoCallProvider = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const VideoCallNote = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.caption)};

  color: ${({ theme }) => theme.palette.text.secondary};
`;

// pulls the text button back in line with the link above it, which carries no button padding
export const VideoCallAction = styled.div`
  margin-left: -8px;
`;
